/**
 * Benefits — dynamic model count.
 *
 * For every `.benefits__text[data-benefits-count-page]` on the page, fetch the
 * linked page in the browser, count its collection items
 * (`.categories-list__item`), and render "<count> <suffix>" into the element.
 *
 * Multiple blocks are fetched in parallel via Promise.all and deduped by URL,
 * so a page that several blocks point at is requested only once. Results are
 * cached in sessionStorage for a short time to avoid refetching on navigation.
 */
(function () {
	'use strict';

	// The section <script> is included per-section, so it can run more than once
	// when several Benefits sections share a page. Bind everything only once —
	// init() works document-wide and covers every section's blocks.
	if (window.__benefitsModelCountBound) return;
	window.__benefitsModelCountBound = true;

	var ELEMENT_SELECTOR = '[data-benefits-count-page]';
	var ITEM_SELECTOR = '.categories-list__item';
	var CACHE_PREFIX = 'benefitsModelCount:';
	var CACHE_TTL = 1000 * 60 * 60 * 6; // 6 hours

	function readCache(url) {
		try {
			var raw = sessionStorage.getItem(CACHE_PREFIX + url);
			if (!raw) return null;
			var parsed = JSON.parse(raw);
			if (!parsed || typeof parsed.count !== 'number') return null;
			if (Date.now() - parsed.t > CACHE_TTL) return null;
			return parsed.count;
		} catch (e) {
			return null;
		}
	}

	function writeCache(url, count) {
		try {
			sessionStorage.setItem(CACHE_PREFIX + url, JSON.stringify({ count: count, t: Date.now() }));
		} catch (e) {
			/* storage full / unavailable — ignore */
		}
	}

	function countModels(html) {
		var doc = new DOMParser().parseFromString(html, 'text/html');
		return doc.querySelectorAll(ITEM_SELECTOR).length;
	}

	function fetchCount(url) {
		var cached = readCache(url);
		if (cached !== null) return Promise.resolve(cached);

		return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.then(function (res) {
				if (!res.ok) throw new Error('HTTP ' + res.status);
				return res.text();
			})
			.then(function (html) {
				var count = countModels(html);
				writeCache(url, count);
				return count;
			});
	}

	function applyCount(el, count) {
		if (typeof count !== 'number' || isNaN(count) || count <= 0) return; // keep fallback text
		var suffix = (el.getAttribute('data-benefits-count-suffix') || '').trim();
		el.textContent = suffix ? count + ' ' + suffix : String(count);
	}

	function init(root) {
		var scope = root || document;
		var els = Array.prototype.slice.call(scope.querySelectorAll(ELEMENT_SELECTOR));
		if (!els.length) return;

		// Group elements by URL so each page is fetched only once.
		var byUrl = {};
		els.forEach(function (el) {
			if (el.dataset.benefitsCountDone === 'true') return;
			var url = el.getAttribute('data-benefits-count-page');
			if (!url) return;
			(byUrl[url] = byUrl[url] || []).push(el);
		});

		var urls = Object.keys(byUrl);
		if (!urls.length) return;

		Promise.all(
			urls.map(function (url) {
				return fetchCount(url)
					.then(function (count) {
						byUrl[url].forEach(function (el) {
							applyCount(el, count);
							el.dataset.benefitsCountDone = 'true';
						});
					})
					.catch(function (err) {
						// Leave the server-rendered fallback text in place.
						if (window.console && console.warn) {
							console.warn('[benefits] model count failed for', url, err);
						}
					});
			})
		);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			init();
		});
	} else {
		init();
	}

	// Re-run for a section re-rendered in the theme editor.
	document.addEventListener('shopify:section:load', function (e) {
		init(e.target);
	});
})();
